import React, { useEffect, useRef, createElement, Fragment, memo } from 'react'
import { createRoot } from 'react-dom/client'
import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js'
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches'
import { Routes } from '@pmi/pmi-iqos-shared-react'
import { Header, ProductCard, ItemLink } from './ui'
import { IndexNameTypes, IndexIdTypes } from './utils'
import { DcxAnalytics } from '@dcx-common/services'

const Autocomplete = ({
  translations,
  onSubmit,
  searchClient,
  sourcesConfiguration,
  updateNoResultsState,
  spiceIdHash,
  ...props
}) => {
  const containerRef = useRef(null)
  const panelRootRef = useRef(null)
  const rootRef = useRef(null)
  const panelContainerRef = useRef(null)

  let noResults = {}

  const setNoResults = (id, value) => {
    noResults = { ...noResults, [id]: value }
    updateNoResultsState(noResults)
  }

  useEffect(() => {
    if (!containerRef.current || !panelContainerRef.current) {
      return undefined
    }

    // Algolia search source definition
    const getSources = ({ query }) =>
      sourcesConfiguration.map(sItem => ({
        sourceId: sItem.indexId || sItem.indexName,
        getItems() {
          // If no query yet started, return empty list
          if (!query) {
            return []
          }
          return getAlgoliaResults({
            searchClient,
            queries: [
              {
                indexName: sItem.indexName,
                params: {
                  query,
                  filters: sItem.filters,
                  ...(sItem.limitAutoSearch && {
                    hitsPerPage: sItem.limitAutoSearch
                  }) // TODO - How to override the default 5?
                }
              }
            ],
            transformResponse: ({ hits }) => {
              // Store no results state
              setNoResults(sItem.indexId || sItem.indexName, !hits[0]?.length)
              hits[0].forEach((h, i) => (h.__position = i))
              return hits
            }
          })
        },
        onSelect: ({ setIsOpen }) => {
          setIsOpen(true)
        },
        templates: {
          header() {
            return <Header text={sItem.text} />
          },
          item({ item, components }) {
            const fireSearchClickGA = () => {
              DcxAnalytics.searchResultClickEvent(
                query || '',
                item.__position,
                item.name || item.title || item.query,
                sItem.indexId || sItem.indexName
              )
            }
            if (sItem.indexName === IndexNameTypes.PRODUCTS) {
              return (
                <ProductCard
                  hit={item}
                  components={components}
                  translations={translations}
                  indexId={sItem.indexId}
                  fireSearchClickGA={fireSearchClickGA}
                />
              )
            }
            return (
              <ItemLink
                hit={item}
                components={components}
                indexName={sItem.indexName}
                indexId={sItem.indexId}
                fireSearchClickGA={fireSearchClickGA}
              />
            )
          }
        }
      }))

    // Recent search source localstorage plugin
    const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
      key: IndexIdTypes.RECENT_SEARCHES,
      limit: 4,
      transformSource({ source, state }) {
        return {
          ...source,
          getItems: state.query ? () => [] : source.getItems, // Hide recent search section when user starts typing
          onSelect: ({ setIsOpen }) => {
            setIsOpen(true)
          },
          getItemUrl: ({ item }) => {
            return `${Routes.searchPage}?query=${item.label}`
          },
          templates: {
            header() {
              return <Header text={translations.tk_recentSearch} />
            },
            item({ item, components }) {
              return (
                <ItemLink
                  hit={item}
                  components={components}
                  indexId={IndexIdTypes.RECENT_SEARCHES}
                />
              )
            }
          }
        }
      }
    })

    // Autocomplete search init
    const search = autocomplete({
      getSources,
      container: containerRef.current,
      panelContainer: panelContainerRef.current,
      placeholder: translations.tk_searchTerm,
      renderer: { createElement, Fragment, render: () => {} },
      render({ children }, root) {
        if (!panelRootRef.current || rootRef.current !== root) {
          rootRef.current = root
          panelRootRef.current?.unmount()
          panelRootRef.current = createRoot(root)
        }
        panelRootRef.current.render(children)
      },
      // autoFocus: true,
      openOnFocus: true,
      debug: true, // Should be false for production, but has the correct behaviour of keep the panel opened after blur
      detachedMediaQuery: 'none',
      insights: {
        insightsInitParams: {
          userToken: spiceIdHash
        }
      },
      onSubmit: ({ state }) => {
        onSubmit(state)
        search.refresh()
      },
      plugins: [recentSearchesPlugin],
      ...props
    })

    search.setIsOpen(true)
    search.refresh()

    return () => {
      search.destroy()
    }
  }, [props])

  return (
    <>
      <div ref={containerRef} />
      <div ref={panelContainerRef} />
    </>
  )
}

export default memo(Autocomplete)
